import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const SIDEBAR_ITEMS = [
  { icon: 'ri-home-smile-line', label: 'Hero & Stats' },
  { icon: 'ri-user-line', label: 'About Preview' },
  { icon: 'ri-message-3-line', label: 'CTA Section' },
  { icon: 'ri-tools-line', label: 'Services' },
  { icon: 'ri-heart-line', label: 'Our Values' },
  { icon: 'ri-list-check', label: 'How It Works' },
  { icon: 'ri-star-line', label: 'Reviews' },
  { icon: 'ri-information-line', label: 'About Us' },
  { icon: 'ri-question-line', label: 'FAQs' },
  { icon: 'ri-file-list-line', label: 'Services Page' },
  { icon: 'ri-mail-line', label: 'Contact Info' },
  { icon: 'ri-settings-line', label: 'General' },
  { icon: 'ri-image-line', label: 'Media' },
  { icon: 'ri-bar-chart-line', label: 'Performance' },
];

export default function AdminPage() {
  const { t } = useTranslation();
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [langTab, setLangTab] = useState<'es' | 'en'>('es');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim() && password.trim()) {
      setLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Por favor ingresa usuario y contraseña');
    }
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="bg-background-200 border border-white/10 rounded-2xl p-8 w-full max-w-md relative">
          <div className="text-center mb-8">
            <div className="w-12 h-12 flex items-center justify-center bg-primary-500/15 rounded-full mx-auto mb-4">
              <i className="ri-shield-user-line text-primary-500 text-2xl" />
            </div>
            <h2 className="text-foreground-50 text-2xl font-bold">{t('admin.loginTitle')}</h2>
            <p className="text-foreground-400 text-sm mt-1">{t('admin.loginDesc')}</p>
          </div>

          <form onSubmit={handleLogin} className="flex flex-col gap-5">
            <div>
              <label className="block text-foreground-50 text-sm font-semibold mb-2">{t('admin.user')}</label>
              <div className="relative">
                <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('admin.userPlaceholder')}
                  className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg pl-11 pr-4 py-3 outline-none focus:border-primary-500 transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-foreground-50 text-sm font-semibold mb-2">{t('admin.password')}</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('admin.passwordPlaceholder')}
                  className="w-full bg-white/5 border border-white/10 text-foreground-50 placeholder-foreground-400 text-sm rounded-lg pl-11 pr-4 py-3 outline-none focus:border-primary-500 transition-all"
                />
              </div>
            </div>
            {loginError && (
              <p className="text-red-400 text-sm">{loginError}</p>
            )}
            <button
              type="submit"
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-3.5 rounded-lg text-sm flex items-center justify-center gap-2 transition-all cursor-pointer"
            >
              <i className="ri-login-box-line" />
              {t('admin.submit')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-background-100 border-r border-white/5 flex-shrink-0 hidden md:flex flex-col">
        <div className="p-5 flex items-center gap-3 border-b border-white/5">
          <i className="ri-shield-user-line text-primary-500 text-xl" />
          <div>
            <span className="text-foreground-50 font-bold text-sm">{t('admin.panelTitle')}</span>
            <span className="text-foreground-400 text-xs ml-2">{t('admin.panelBrand')}</span>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {SIDEBAR_ITEMS.map((item, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`w-full flex items-center gap-3 px-5 py-3 text-sm transition-colors cursor-pointer ${
                activeTab === i
                  ? 'text-primary-500 bg-primary-500/10 border-r-2 border-primary-500'
                  : 'text-foreground-400 hover:text-foreground-50 hover:bg-white/5'
              }`}
            >
              <i className={`${item.icon} w-5 h-5 flex items-center justify-center`} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <p className="text-foreground-400/50 text-[10px]">Los cambios se reflejan al recargar la página pública.</p>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-background-100 border-b border-white/5 flex items-center justify-between px-6">
          <h1 className="text-foreground-50 font-bold text-lg">{SIDEBAR_ITEMS[activeTab]?.label}</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLangTab('es')}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors cursor-pointer ${
                langTab === 'es' ? 'bg-primary-500 text-white' : 'bg-white/5 text-foreground-400'
              }`}
            >
              ES
            </button>
            <button
              onClick={() => setLangTab('en')}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors cursor-pointer ${
                langTab === 'en' ? 'bg-primary-500 text-white' : 'bg-white/5 text-foreground-400'
              }`}
            >
              EN
            </button>
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-400 hover:text-foreground-50 text-sm transition-colors cursor-pointer"
            >
              <i className="ri-eye-line" />
              {t('admin.viewSite')}
            </Link>
            <button
              onClick={() => setLoggedIn(false)}
              className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-line" />
              {t('admin.logout')}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="max-w-3xl mx-auto">
            <div className="text-foreground-400 text-sm mb-6">
              Editando en {langTab === 'es' ? 'Español' : 'Inglés'} · Namespace: <span className="text-primary-500">home</span>
            </div>

            {/* Placeholder fields */}
            <div className="space-y-6">
              <div className="bg-background-200 border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-foreground-50 font-semibold">Insignia del Hero</span>
                  <span className="text-foreground-400 text-xs">(hero_badge)</span>
                </div>
                <input
                  className="w-full bg-white/5 border border-white/10 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all"
                  defaultValue={langTab === 'es' ? 'Servicios Profesionales de Aislamiento' : 'Professional Insulation Services'}
                />
              </div>

              <div className="bg-background-200 border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-foreground-50 font-semibold">Título Principal</span>
                  <span className="text-foreground-400 text-xs">(hero_title)</span>
                </div>
                <textarea
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all resize-none"
                  defaultValue={langTab === 'es' ? 'Soluciones de Aislamiento para Hogares y Negocios' : 'Insulation Solutions for Homes & Businesses'}
                />
              </div>

              <div className="bg-background-200 border border-white/5 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-foreground-50 font-semibold">Descripción del Hero</span>
                  <span className="text-foreground-400 text-xs">(hero_desc)</span>
                </div>
                <textarea
                  rows={4}
                  className="w-full bg-white/5 border border-white/10 text-foreground-50 text-sm rounded-lg px-4 py-3 outline-none focus:border-primary-500 transition-all resize-none"
                  defaultValue={langTab === 'es' ? 'FMO LLC se especializa en aislamiento de espuma spray, paneles, celulosa, soplado, ático, paredes y sótanos. Más de 15 años de experiencia mejorando la eficiencia energética y el confort en Lafayette, Indiana.' : 'FMO LLC specializes in spray foam, batt, cellulose, blown-in, attic, wall, and crawl space insulation. Over 15 years of experience improving energy efficiency and comfort in Lafayette, Indiana.'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}